import { Card, Form, Input, Typography } from 'antd';
import { autoDescription } from '@newsmedia/shared';

export interface SeoState {
  metaTitle?: string;
  metaDescription?: string;
  canonical?: string;
  focusKeyword?: string;
}

interface Props {
  value: SeoState;
  onChange: (next: SeoState) => void;
  fallbackTitle: string;
  /** 內文與舊站摘要：SEO 描述留空時，前台用「舊站摘要 → 內文第一段」，這裡照同樣規則預覽。 */
  bodyJson: string | null;
  excerpt: string;
  /** OG 圖自動跟封面走（不手動填）。 */
  coverUrl?: string | null;
}

/**
 * SEO 設定 + 分享預覽（OG 圖＝封面、標題＝文章標題）。
 * 客戶 10/25：SEO 標題一律用文章標題（展名、場館名填在「標籤」），焦點關鍵字不顯示——兩欄都拿掉。
 */
export default function SeoPanel({ value, onChange, fallbackTitle, bodyJson, excerpt, coverUrl }: Props) {
  const set = (k: keyof SeoState) => (e: { target: { value: string } }) =>
    onChange({ ...value, [k]: e.target.value });

  const autoDesc = excerpt || autoDescription(bodyJson);
  const ogDesc = value.metaDescription || autoDesc;

  return (
    <Card title="SEO / 社群分享">
      <Form layout="vertical">
        <Form.Item
          label="SEO 描述（選填）"
          className="!mb-0"
          extra="留空會自動用內文第一段（約 120 字）。Google 搜尋結果大約只顯示 80～120 字，超過的部分會被截掉，字數僅供參考。"
        >
          <Input.TextArea
            autoSize={{ minRows: 3 }}
            value={value.metaDescription}
            onChange={set('metaDescription')}
            placeholder={autoDesc ? `（自動）${autoDesc}` : '留空會自動用內文第一段'}
          />
        </Form.Item>
      </Form>

      <div className="mt-4 text-sm font-medium text-[#1f2733]">分享預覽（FB / LINE）</div>
      <Typography.Text type="secondary" className="text-xs">
        自動產生，不用另外設定：圖片＝封面照片（舊站搬來的文章若原本有指定分享圖，沿用原圖），標題＝文章標題，說明＝SEO 描述（空白則用內文第一段）
      </Typography.Text>
      <div className="mt-1 overflow-hidden rounded-md border">
        {coverUrl ? (
          <img src={coverUrl} alt="og" className="h-32 w-full object-cover" />
        ) : (
          <div className="flex h-32 items-center justify-center bg-gray-100 text-gray-400">
            尚未設定封面
          </div>
        )}
        <div className="p-2">
          <div className="truncate text-sm font-medium">{fallbackTitle || '（未填標題）'}</div>
          <div className="line-clamp-2 text-xs text-gray-500">{ogDesc || '（無描述）'}</div>
          <div className="mt-1 text-xs uppercase text-gray-400">newsmedia.today</div>
        </div>
      </div>
    </Card>
  );
}
